import {
  Injector,
  Signal,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import {
  FieldTree,
  SchemaPath,
  disabled,
  form,
  required,
} from '@angular/forms/signals';

export type LookupSelections<T> = T[] | T | null;

export interface LookupList<T> {
  entries: Signal<FieldTree<T | null>[]>;
  seed(): void;
  add(): void;
}

export function selectionList<T extends object>(
  value: LookupSelections<T> | undefined
): T[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is T => !!entry);
  }
  return value ? [value] : [];
}

export function lookupList<T extends object>(
  field: () => FieldTree<LookupSelections<T>>,
  sameSelection: (a: T, b: T) => boolean
): LookupList<T> {
  const injector = inject(Injector);
  const state = computed(() => field()());
  const entries = signal<FieldTree<T | null>[] | null>(null);

  const createEntry = (value: T | null, first: boolean) =>
    form(
      signal<T | null>(value),
      tree => {
        const path = tree as SchemaPath<T | null>;
        disabled(path, { when: () => state().disabled() });
        if (first) required(path, { when: () => state().required() });
      },
      { injector }
    );

  effect(() => {
    const current = entries();
    if (!current) return;
    const picked = current
      .map(entry => entry().value())
      .filter((value): value is T => !!value);
    untracked(() => {
      const host = state();
      const saved = selectionList(host.value());
      if (
        saved.length === picked.length &&
        saved.every((value, i) => sameSelection(value, picked[i]))
      ) {
        return;
      }
      host.value.set(picked.length ? picked : null);
      host.markAsDirty();
    });
  });

  effect(() => {
    if (!state().touched()) return;
    untracked(() => entries()?.forEach(entry => entry().markAsTouched()));
  });

  return {
    entries: computed(() => entries() ?? []),
    seed() {
      const saved = selectionList(state().value());
      const values = saved.length ? saved : [null];
      entries.set(values.map((value, i) => createEntry(value, i === 0)));
    },
    add() {
      entries.update(current => [
        ...(current ?? []),
        createEntry(null, !current?.length),
      ]);
    },
  };
}
